import React from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, DoorOpen, UserPlus, Package, ClipboardList,
  IdCard, ShieldCheck, ScanLine, FileBarChart2, Settings, LogOut,
  Bell, Menu, X, UserCheck, KeyRound, Users,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useEffect, useState } from 'react';
import api from '../api/client';

const NAV = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard, roles: ['admin','hr','supervisor','guard','employee'] },
  { to: '/gate-passes', label: 'My Passes',   icon: DoorOpen, roles: ['admin', 'hr', 'supervisor'] },
  { to: '/gate-passes', label: 'Gate Passes', icon: DoorOpen, roles: ['guard','employee'] },
  { to: '/visitors', label: 'Visitors', icon: UserPlus, roles: ['hr','guard'] },
  { to: '/visitor-requests', label: 'Visitor Requests', icon: UserCheck, roles: ['admin','hr','supervisor','employee'] },
  { to: '/item-passes', label: 'Item Passes', icon: Package, roles: ['hr','guard','employee'] },
  { to: '/candidates', label: 'Candidates', icon: ClipboardList, roles: ['hr'] },
  { to: '/monthly-passes', label: 'Monthly Passes', icon: IdCard, roles: ['hr','employee'] },
  { to: '/monthly-passes', label: 'My Monthly Pass', icon: IdCard, roles: ['supervisor'] },
  { to: '/approvals', label: 'Pending Approvals', icon: ShieldCheck, roles: ['admin','supervisor'] },
  { to: '/my-team', label: 'My Team', icon: Users, roles: ['supervisor'] },
  { to: '/scanner', label: 'QR Scanner', icon: ScanLine, roles: ['guard'] },
  { to: '/reports', label: 'Reports', icon: FileBarChart2, roles: ['admin'] },
  { to: '/admin', label: 'Admin', icon: Settings, roles: ['admin'] },
];

// Supervisors get their own explicit order (mirrors the mobile supervisor experience).
// Every other role keeps the NAV array order above.
const ROLE_ORDER = {
  supervisor: ['/', '/approvals', '/visitor-requests', '/gate-passes', '/monthly-passes', '/my-team'],
};

const ROLE_COLORS = {
  admin: 'bg-zinc-900 text-white',
  hr: 'bg-blue-600 text-white',
  supervisor: 'bg-purple-600 text-white',
  guard: 'bg-green-700 text-white',
  employee: 'bg-zinc-600 text-white',
};

export default function Shell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);

  const fetchUnread = async () => {
    try {
      const { data } = await api.get('/notifications');
      setUnread(data.filter((n) => !n.read).length);
    } catch {}
  };

  useEffect(() => {
    fetchUnread();
    const id = setInterval(fetchUnread, 15000);
    return () => clearInterval(id);
  }, []);

  const onLogout = async () => {
    await logout();
    navigate('/login');
  };

  const order = ROLE_ORDER[user?.role];
  const items = NAV
    .filter((n) => n.roles.includes(user?.role))
    .sort((a, b) => (order ? order.indexOf(a.to) - order.indexOf(b.to) : 0));

  return (
    <div className="min-h-screen flex bg-zinc-50">
      {/* Sidebar */}
      <aside className={`fixed md:static z-40 inset-y-0 left-0 w-64 bg-white border-r border-zinc-200 transform ${mobileOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0 transition-transform`} data-testid="sidebar">
        <div className="h-16 px-5 flex items-center gap-2 border-b border-zinc-200">
          <img
            src={`${process.env.PUBLIC_URL}/logo192.png`}
            alt="Unikin"
            className="w-8 h-8 object-contain"
            data-testid="brand-icon"
          />
          <div className="leading-none">
            <div className="heading font-black text-base text-ink lowercase">unikin</div>
            <div className="text-[10px] tracking-[0.2em] text-zinc-500 font-semibold uppercase">Ops Control</div>
          </div>
        </div>
        <nav className="p-3 flex flex-col gap-0.5" data-testid="sidebar-nav">
          {items.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === '/'}
              data-testid={`nav-${n.to.replace('/', '') || 'dashboard'}`}
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 text-sm rounded-sm font-medium ${
                  isActive ? 'bg-zinc-900 text-white' : 'text-zinc-700 hover:bg-zinc-100'
                }`
              }
            >
              <n.icon size={16} strokeWidth={2.2} />
              <span>{n.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="absolute bottom-0 left-0 right-0 border-t border-zinc-200 p-3">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className={`w-9 h-9 rounded-sm flex items-center justify-center font-bold text-sm ${ROLE_COLORS[user?.role] || 'bg-zinc-600 text-white'}`}>
              {(user?.name || '?').slice(0, 1).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-ink truncate" data-testid="user-name">{user?.name}</div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold" data-testid="user-role">{user?.role}</div>
            </div>
            <NavLink to="/settings" className="btn-ghost p-1.5" title="Settings" data-testid="settings-btn" onClick={() => setMobileOpen(false)}>
              <KeyRound size={16} />
            </NavLink>
            <button onClick={onLogout} className="btn-ghost p-1.5" title="Logout" data-testid="logout-btn">
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </aside>

      {/* Content */}
      <div className="flex-1 flex flex-col md:pl-0 w-full">
        <header className="h-16 bg-white border-b border-zinc-200 flex items-center px-4 md:px-6 gap-4">
          <button className="md:hidden btn-ghost p-2" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-toggle">
            {mobileOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
          <div className="flex-1">
            <div className="overline hidden md:block">Industrial Gate & Visitor Management</div>
            <div className="heading font-bold text-ink text-lg md:text-xl">Welcome back, {user?.name?.split(' ')[0]}</div>
          </div>
          <NavLink to="/notifications" className="relative btn-outline" data-testid="notifications-btn">
            <Bell size={16} />
            {unread > 0 && (
              <span className="absolute -top-1 -right-1 bg-primary text-white text-[10px] rounded-full w-5 h-5 flex items-center justify-center font-bold" data-testid="unread-count">
                {unread}
              </span>
            )}
          </NavLink>
        </header>

        <main className="flex-1 p-4 md:p-8 max-w-[1600px] w-full mx-auto" data-testid="main-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
